'use client';

import React from 'react';
import { Wallet } from 'lucide-react';
import { css } from 'styled-system/css';
import type { Address } from 'viem';
import { formatUnits } from 'viem';
import { mainnet } from 'viem/chains';
import { useBalance, useConnection, useDisconnect } from 'wagmi';

import { useConnectWallet, usePrivy } from '@privy-io/react-auth';
import ShaojiangDropdown, { type DropdownOption } from '@shaojiang/components/ShaojiangDropdown';
import Tooltip from '@shaojiang/components/Tooltip';
import { IconButton } from '@shaojiang/components/ui/icon-button';
import { useTokenBalances } from '@shaojiang/hooks/useTokenBalances';
import { toaster } from '@shaojiang/lib/toaster';
import { formatAddress, formatBalance, getNetworkName } from '@shaojiang/utils/web3';

/** Wallet connect (Privy): an icon in the nav; once connected, a menu with balances. */
export default function Connect2Wallet({ label = 'Wallet' }: { label?: string }) {
  const { ready } = usePrivy();
  const { connectWallet } = useConnectWallet();
  const { address, isConnected, chain } = useConnection();
  const { disconnect } = useDisconnect();

  // ETH balance on Ethereum mainnet
  const { data: ethBalance, isLoading: isLoadingEth } = useBalance({
    address: address as Address | undefined,
    chainId: mainnet.id,
    query: { enabled: !!address && isConnected },
  });

  // ERC-20 balances, read directly from the token contracts
  const { tokens, isLoading: isLoadingTokens } = useTokenBalances(address, mainnet.id);

  if (!ready) {
    return null;
  }

  const handleConnect = async () => {
    try {
      await connectWallet();
    } catch (err) {
      toaster.create({
        type: 'error',
        title: 'Wallet',
        description: err instanceof Error ? err.message : 'Failed to connect wallet',
      });
    }
  };

  const ethText = ethBalance
    ? `${formatBalance(formatUnits(ethBalance.value, ethBalance.decimals))} ${ethBalance.symbol}`
    : 'No balance';

  const menuItems: DropdownOption[] =
    isConnected && address
      ? [
          { label: `Address: ${formatAddress(address)}`, value: 'address', disabled: true },
          {
            label: chain ? `Network: ${getNetworkName(chain.id)}` : 'Unknown Network',
            value: 'network',
            disabled: true,
          },
          ...(ethBalance
            ? [
                {
                  label: `ETH: ${formatBalance(formatUnits(ethBalance.value, ethBalance.decimals))}`,
                  value: 'eth-balance',
                  disabled: true,
                },
              ]
            : []),
          // Top 5 ERC-20 balances, to keep the menu short
          ...tokens.slice(0, 5).map((token, index) => ({
            label: `${token.symbol}: ${formatBalance(token.balanceFormatted)}`,
            value: `token-${index}`,
            disabled: true,
          })),
          ...(tokens.length > 5
            ? [
                {
                  label: `+ ${tokens.length - 5} more tokens`,
                  value: 'more-tokens',
                  disabled: true,
                },
              ]
            : []),
          { label: 'Disconnect', value: 'disconnect' },
        ]
      : [];

  if (!isConnected) {
    return (
      <Tooltip title='Connect Wallet'>
        <IconButton variant='surface' size='sm' aria-label='Connect wallet' onClick={handleConnect}>
          <Wallet />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <ShaojiangDropdown
      tooltip={isLoadingEth || isLoadingTokens ? 'Loading...' : ethText}
      menus={menuItems}
      onChange={(value) => value === 'disconnect' && disconnect()}
    >
      <IconButton variant='surface' size='sm' aria-label={label}>
        <Wallet className={css({ color: 'fg.accent' })} />
      </IconButton>
    </ShaojiangDropdown>
  );
}
